import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const inputCls = "w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm";
const TYPES = [
    { value: "every_minutes", label: "Every N minutes" },
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "cron", label: "Cron expression" },
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function emptySchedule() {
    return { type: "daily", every_minutes: 15, minute: 0, hour: 6, weekday: 1, cron: "0 6 * * 1-5", timezone: "UTC" };
}

/** Presets + cron builder with a live "next runs" preview from the API. */
export default function ScheduleEditor({ value, onChange, timezones = ["UTC"] }) {
    const s = { ...emptySchedule(), ...(value || {}) };
    const set = (patch) => onChange({ ...s, ...patch });
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState(null);

    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/admin/scheduled-tasks/preview", { schedule: s, count: 3 });
                setPreview(data); setPreviewErr(null);
            } catch (e) {
                setPreview(null); setPreviewErr(formatApiErrorDetail(e?.response?.data?.detail));
            }
        }, 300);
        return () => clearTimeout(t);
    }, [s.type, s.every_minutes, s.minute, s.hour, s.weekday, s.cron, s.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-3" data-testid="schedule-editor">
            <div className="flex flex-wrap gap-1.5">
                {TYPES.map((t) => (
                    <button key={t.value} type="button" onClick={() => set({ type: t.value })} data-testid={`schedule-type-${t.value}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${s.type === t.value
                            ? "bg-[#7C3AED]/20 border-[#7C3AED]/60 text-[#c4b5fd]" : "border-white/10 text-white/55 hover:text-white"}`}>
                        {t.label}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {s.type === "every_minutes" && (
                    <Num label="Every … minutes" value={s.every_minutes} min={1} max={1440} onChange={(v) => set({ every_minutes: v })} testId="schedule-every-minutes" />
                )}
                {s.type === "weekly" && (
                    <label className="block">
                        <div className="text-[11px] text-white/55 mb-1">Weekday</div>
                        <select value={s.weekday} onChange={(e) => set({ weekday: Number(e.target.value) })} data-testid="schedule-weekday" className={inputCls}>
                            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                        </select>
                    </label>
                )}
                {(s.type === "daily" || s.type === "weekly") && (
                    <Num label="Hour" value={s.hour} min={0} max={23} onChange={(v) => set({ hour: v })} testId="schedule-hour" />
                )}
                {(s.type === "hourly" || s.type === "daily" || s.type === "weekly") && (
                    <Num label="Minute" value={s.minute} min={0} max={59} onChange={(v) => set({ minute: v })} testId="schedule-minute" />
                )}
                {s.type === "cron" && (
                    <label className="block col-span-2">
                        <div className="text-[11px] text-white/55 mb-1">Cron (minute hour day month weekday)</div>
                        <input value={s.cron} onChange={(e) => set({ cron: e.target.value })} placeholder="*/30 8-18 * * mon-fri"
                            data-testid="schedule-cron" className={`${inputCls} font-mono`} />
                    </label>
                )}
                {s.type !== "every_minutes" && (
                    <label className="block">
                        <div className="text-[11px] text-white/55 mb-1">Timezone</div>
                        <select value={s.timezone} onChange={(e) => set({ timezone: e.target.value })} data-testid="schedule-timezone" className={inputCls}>
                            {[...new Set([s.timezone, ...timezones])].map((z) => <option key={z} value={z}>{z}</option>)}
                        </select>
                    </label>
                )}
            </div>
            <div className="text-[11px] font-mono rounded-md border border-white/10 bg-black/30 px-3 py-2" data-testid="schedule-preview">
                {previewErr ? <span className="text-rose-200" data-testid="schedule-preview-error">{previewErr}</span>
                    : preview ? (
                        <>
                            <span className="text-white/80">{preview.schedule_text}</span>
                            <span className="text-white/40"> · next: {preview.next_runs.map((t) => t.slice(0, 16).replace("T", " ")).join(" · ")} UTC</span>
                        </>
                    ) : <span className="text-white/40">computing…</span>}
            </div>
        </div>
    );
}

function Num({ label, value, min, max, onChange, testId }) {
    return (
        <label className="block">
            <div className="text-[11px] text-white/55 mb-1">{label}</div>
            <input type="number" min={min} max={max} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                data-testid={testId} className={`${inputCls} font-mono`} />
        </label>
    );
}
